import { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import {
  AssetsByClaimedDateResponse,
  ClaimPolicyMonetisationResponse,
  ClaimPolicyPatchSummaryResponse,
  claimPolicyPatchDashboardApi,
} from "../../../api/claimPolicyPatchDashboardApi";
import { classNames } from "primereact/utils";

const MONTH_WINDOW_OPTIONS = [
  { label: "3 months", value: 3 },
  { label: "6 months", value: 6 },
  { label: "12 months", value: 12 },
  { label: "24 months", value: 24 },
];

const SUMMARY_WINDOW_DAYS = 30;
const MAX_VIDEO_ROWS = 100;

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value ?? 0);

const formatMoney = (value: number): string =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

interface KpiCardProps {
  label: string;
  value: string;
  subValue: string;
  accentColor: string;
  className?: string;
}

const KpiCard = ({ label, value, subValue, accentColor, className }: KpiCardProps) => (
  <div
    className={classNames("claim-policy-patch-dashboard-kpi-card", className)}
    style={{
      flex: 1,
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
      borderTop: `3px solid ${accentColor}`,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}
  >
    <p className="kpi-card-label" style={{ margin: 0, fontSize: "11px", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af" }}>
      {label}
    </p>
    <p className="kpi-card-label" style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#1f2937" }}>
      {value}
    </p>
    <p className="kpi-card-label" style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>{subValue}</p>
  </div>
);

const ClaimPolicyPatchOverview = () => {
  const [monthWindow, setMonthWindow] = useState<number>(12);
  const [summary, setSummary] = useState<ClaimPolicyPatchSummaryResponse | null>(null);
  const [monetisation, setMonetisation] = useState<ClaimPolicyMonetisationResponse | null>(null);
  const [assetsByClaimedDate, setAssetsByClaimedDate] = useState<AssetsByClaimedDateResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryResponse, monetisationResponse, assetsResponse] = await Promise.all([
          claimPolicyPatchDashboardApi.getSummary(SUMMARY_WINDOW_DAYS),
          claimPolicyPatchDashboardApi.getMonetisation(monthWindow),
          claimPolicyPatchDashboardApi.getAssetsByClaimedDate(monthWindow),
        ]);
        if (!active) return;
        setSummary(summaryResponse);
        setMonetisation(monetisationResponse);
        setAssetsByClaimedDate(assetsResponse);
      } catch (loadError) {
        console.error("Failed to load claim policy patch dashboard", loadError);
        if (active) setError("Unable to load dashboard data right now.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [monthWindow]);

  const monthlyRows = useMemo(() => monetisation?.byMonth ?? [], [monetisation?.byMonth]);
  const topVideoRows = useMemo(
    () => (monetisation?.byVideo ?? []).slice(0, MAX_VIDEO_ROWS),
    [monetisation?.byVideo]
  );
  const assetBuckets = useMemo(() => assetsByClaimedDate?.buckets ?? [], [assetsByClaimedDate?.buckets]);

  return (
    <div style={{ padding: "28px 32px 40px", display: "flex", flexDirection: "column", gap: "28px" }}>
      <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
        Patching volume and monetisation since claim date for patched videos.
      </p>

      <div style={{ display: "flex", gap: "16px" }}>
        <KpiCard
          label="Patched Claims"
          value={formatNumber(summary?.totalPatchedClaims ?? 0)}
          subValue={`${formatNumber(summary?.patchedClaimsInWindow ?? 0)} in last ${SUMMARY_WINDOW_DAYS} days`}
          accentColor="#6366f1"
        />
        <KpiCard
          label="Patched Videos"
          value={formatNumber(summary?.totalPatchedVideos ?? 0)}
          subValue={`${formatNumber(summary?.patchedVideosInWindow ?? 0)} in last ${SUMMARY_WINDOW_DAYS} days`}
          accentColor="#10b981"
        />
        <KpiCard
          label="Total Monetisation (Since Claim)"
          value={formatMoney(monetisation?.totalMonetisation ?? 0)}
          subValue={`Last synced: ${monetisation?.lastSyncedAt ?? summary?.lastMonetisationSyncAt ?? "N/A"}`}
          accentColor="#f59e0b"
        />
      </div>

      {error && (
        <div className="p-tag-warning" style={{ padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#b91c1c", fontSize: "13px" }}>
          {error}
        </div>
      )}

      <Divider style={{ margin: 0 }} />

      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <span style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af", whiteSpace: "nowrap" }}>
          Monetisation Window
        </span>
        <SelectButton
          value={monthWindow}
          onChange={(e) => e.value !== null && setMonthWindow(e.value)}
          options={MONTH_WINDOW_OPTIONS}
          optionLabel="label"
          optionValue="value"
        />
      </div>

      {loading ? (
        <Card>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <ProgressSpinner />
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div style={{ display: "flex", gap: "20px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="kpi-card-label" style={{ margin: "0 0 10px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                Monetisation by Month
              </p>
              <Card>
                <DataTable
                  value={monthlyRows}
                  emptyMessage="No monthly monetisation records found."
                  stripedRows
                  size="small"
                  scrollable
                  scrollHeight="320px"
                >
                  <Column field="month" header="Month" sortable />
                  <Column
                    field="total"
                    header="Monetisation"
                    body={(row) => formatMoney(row.total)}
                    sortable
                    align="right"
                    headerStyle={{ textAlign: "right" }}
                  />
                </DataTable>
              </Card>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="kpi-card-label" style={{ margin: "0 0 10px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                Assets by Claimed Date
                <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 400, color: "#9ca3af" }}>
                  {formatNumber(assetsByClaimedDate?.totalAssets ?? 0)} total in window
                </span>
              </p>
              <Card>
                <DataTable
                  value={assetBuckets}
                  emptyMessage="No assets found for claimed-date buckets."
                  stripedRows
                  size="small"
                  scrollable
                  scrollHeight="320px"
                >
                  <Column field="claimMonth" header="Claim Month" sortable />
                  <Column
                    field="assetCount"
                    header="Asset Count"
                    body={(row) => formatNumber(row.assetCount)}
                    sortable
                    align="right"
                    headerStyle={{ textAlign: "right" }}
                  />
                </DataTable>
              </Card>
            </div>
          </div>

          <div>
            <p className="kpi-card-label" style={{ margin: "0 0 10px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>
              Per-Video Monetisation
              <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 400, color: "#9ca3af" }}>
                Top {MAX_VIDEO_ROWS} · sorted by total since claim
              </span>
            </p>
            <Card>
              <DataTable
                value={topVideoRows}
                emptyMessage="No per-video monetisation records found."
                stripedRows
                size="small"
                scrollable
                scrollHeight="420px"
                paginator
                rows={25}
                rowsPerPageOptions={[25, 50, 100]}
              >
                <Column
                  field="videoId"
                  header="Video ID"
                  body={(row) => (
                    <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{row.videoId}</span>
                  )}
                />
                <Column field="claimId" header="Claim ID" body={(row) => row.claimId ?? "—"} />
                <Column field="claimedAt" header="Claimed At" body={(row) => row.claimedAt ?? "—"} />
                <Column
                  field="totalSinceClaim"
                  header="Total Since Claim"
                  body={(row) => formatMoney(row.totalSinceClaim)}
                  sortable
                  align="right"
                  headerStyle={{ textAlign: "right" }}
                />
                <Column
                  header="Months Tracked"
                  body={(row) => formatNumber(row.monthly.length)}
                  align="right"
                  headerStyle={{ textAlign: "right" }}
                />
              </DataTable>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimPolicyPatchOverview;
